import { useEffect, useState } from "react";

import {
  ListContainer,
  ListContext,
  Page,
  PageRoute,
  SearchBar,
  buildPath,
  buildTemplatePath,
  pn,
} from "@compass/core";

import { ListFooter } from "components";
import { ListFilterBase, Permission, SuppliesDetailListDto, SuppliesDetailListResource } from "generated/openApi";
import { useInitListFilter, useList } from "hooks";
import { Outlet } from "react-router-dom";
import ApiService from "services/apiService";
import { SuppliesDetailListPageHeader } from "./SuppliesDetailListPageHeader";
import { SuppliesDetailTable } from "./SuppliesDetailTable";

const routeName = "suppliesDetails";

export const suppliesDetailListPageRoute: PageRoute = {
  handle: "Supplies Details",
  templatePath: buildTemplatePath(routeName),
  getPath: () => buildPath(routeName),
};

export const SuppliesDetailListPage = () => {
  const ROOT_PERMISSION: Permission = Permission.MatchUtility;

  const [listItems, setListItems] = useState<SuppliesDetailListDto[]>([]);
  const { listFilter, setListFilter } = useInitListFilter<ListFilterBase>({
    sortObjects: [{ property: pn<SuppliesDetailListDto>("name"), isDesc: false }],
  });
  const [total, setTotal] = useState<number>();
  const [resource, setResource] = useState<SuppliesDetailListResource>({});
  const [searchFocused, setSearchFocused] = useState<boolean>(false);

  const { tableContainerRef, applyPage, applyPageSize, loadList, applySort, applySearch, applySortMethod } = useList({
    listFilter: listFilter,
    setListFilter: setListFilter,
    fetchList: (listFilter) => {
      ApiService.suppliesDetails.getList(listFilter).then((response) => {
        setListItems(response);
      });
    },
    fetchTotal: (listFilter) => {
      setTotal(undefined);
      ApiService.suppliesDetails.getListTotal(listFilter).then((response) => {
        setTotal(response);
      });
    },
  });

  useEffect(() => {
    loadList(listFilter);

    ApiService.suppliesDetails.getListResource().then((response) => {
      setResource(response);
    });
  }, []);

  return (
    <>
      <Page
        header={
          <SuppliesDetailListPageHeader rootPermission={ROOT_PERMISSION} handle={suppliesDetailListPageRoute.handle} />
        }
      >
        <ListContainer
          fullHeight
          header={<SearchBar onStoppedTyping={applySearch} setSearchFocused={setSearchFocused} size="small" />}
          tableContainer={
            <SuppliesDetailTable
              rootPermission={ROOT_PERMISSION}
              tableContainerRef={tableContainerRef}
              suppliesDetails={listItems}
              setSuppliesDetails={setListItems}
              resource={resource}
              searchFocused={searchFocused}
              sortObjects={listFilter.sortObjects}
              sort={applySort}
              loadList={() => loadList(listFilter)}
            />
          }
          footer={
            <ListFooter
              total={total}
              size={listFilter.pageSize}
              page={listFilter.page}
              onPageChange={applyPage}
              onSizeChange={applyPageSize}
              onSortMethodChange={applySortMethod}
            />
          }
        />
      </Page>

      <ListContext.Provider value={{ loadList: (options) => loadList(listFilter, { resetPage: options?.resetPage }) }}>
        <Outlet />
      </ListContext.Provider>
    </>
  );
};
